import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { useUser } from "@/hooks/use-user";
import { ContentManagementGrid } from "@/components/dashboard/ContentManagementGrid";
import { AnalyticsDashboard } from "@/components/dashboard/AnalyticsDashboard";

interface Pin {
  id: string;
  title: string | null;
  description: string | null;
  image_url: string;
  created_at: string;
  archived: boolean;
  category?: string;
}

interface AnalyticsData {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  sales: number;
}

const MyPins = () => {
  // Use navigate function conditionally to avoid the "cannot read properties of null" error
  const navigate = useNavigate();
  const [pins, setPins] = useState<Pin[]>([]);
  const [activePins, setActivePins] = useState<Pin[]>([]);
  const [archivedPins, setArchivedPins] = useState<Pin[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true);
  const { user, isLoading: isUserLoading } = useUser();
  
  const fetchPins = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      console.log('Buscando pins do usuário ID:', user.id);
      
      // Buscar da tabela pins
      const { data: fetchedPins, error } = await supabase
        .from('pins')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Erro ao buscar pins:', error);
        throw error;
      }
      
      console.log('Pins encontrados:', fetchedPins?.length || 0, fetchedPins);
      
      if (fetchedPins && fetchedPins.length > 0) {
        // Formatar pins para o grid
        const formattedPins = fetchedPins.map(pin => ({
          id: pin.id,
          title: pin.title || 'Pin sem título',
          description: pin.description || '',
          image_url: pin.image_url,
          created_at: pin.created_at,
          archived: pin.archived || false,
          category: pin.category
        }));
        
        setPins(formattedPins);
        setActivePins(formattedPins.filter(pin => !pin.archived));
        setArchivedPins(formattedPins.filter(pin => pin.archived));
      } else {
        // Se não encontrou pins, tente buscar de posts para compatibilidade
        console.log('Nenhum pin encontrado, verificando tabela posts...');
        const { data: postsPins, error: postsError } = await supabase
          .from('posts')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (postsError) {
          console.error('Erro ao buscar posts:', postsError);
        } else if (postsPins && postsPins.length > 0) {
          console.log('Posts encontrados:', postsPins.length);
          
          // Converter posts para formato de pins
          const formattedPins = postsPins.map(post => ({
            id: post.id,
            title: post.title || 'Post sem título',
            description: post.description || '',
            image_url: post.image_url,
            created_at: post.created_at,
            archived: false, // posts não têm campo archived
            category: post.category
          }));
          
          setPins(formattedPins);
          setActivePins(formattedPins);
          setArchivedPins([]);
          toast.success('Seus posts foram carregados');
        } else {
          console.log('Nenhum pin ou post encontrado para este usuário');
          setPins([]);
          setActivePins([]);
          setArchivedPins([]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar pins:', error);
      toast.error('Erro ao carregar seus pins');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchAnalyticsData = async () => {
    if (!user?.id || pins.length === 0) {
      // Gerar dados fictícios se não houver pins para mostrar algo na interface
      generateMockAnalytics();
      return;
    }
    
    setIsAnalyticsLoading(true);
    try {
      // Tentar buscar dados analíticos reais da tabela pin_analytics
      const { data: pinAnalytics, error: analyticsError } = await supabase
        .from('pin_analytics')
        .select('*')
        .in('pin_id', pins.map(pin => pin.id))
        .gte('date', format(subDays(new Date(), 7), 'yyyy-MM-dd'))
        .order('date', { ascending: true });
      
      if (analyticsError || !pinAnalytics || pinAnalytics.length === 0) {
        // Se não houver dados analíticos reais, gerar dados fictícios
        generateMockAnalytics();
      } else {
        // Processar dados analíticos reais
        console.log('Dados analíticos encontrados:', pinAnalytics.length);
        const processedData = pinAnalytics.map(item => ({
          date: format(new Date(item.date), 'yyyy-MM-dd'),
          views: item.views || 0,
          likes: item.likes || 0, 
          comments: item.comments || 0,
          shares: item.shares || 0,
          sales: item.sales || 0,
        }));
        setAnalyticsData(processedData);
      }
    } catch (error) {
      console.error('Erro ao buscar dados analíticos:', error);
      toast.error('Erro ao carregar dados analíticos');
      generateMockAnalytics();
    } finally {
      setIsAnalyticsLoading(false);
    }
  };
  
  const generateMockAnalytics = () => {
    const mockData: AnalyticsData[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      mockData.push({
        date,
        views: Math.floor(Math.random() * 100),
        likes: Math.floor(Math.random() * 50),
        comments: Math.floor(Math.random() * 20),
        shares: Math.floor(Math.random() * 10),
        sales: Math.floor(Math.random() * 5),
      });
    }
    setAnalyticsData(mockData);
  };
  
  useEffect(() => {
    // Verificar se o usuário está logado antes de buscar os dados
    if (!isUserLoading) {
      if (user?.id) {
        fetchPins();
      } else if (navigate) {
        navigate('/auth');
        toast.error("Você precisa estar logado para acessar esta página");
      }
    }
  }, [user, isUserLoading, navigate]);

  // Buscar dados analíticos quando os pins estiverem carregados
  useEffect(() => {
    if (!isLoading && user?.id) {
      fetchAnalyticsData();
    }
  }, [pins, isLoading]);

  const handleArchive = async (id: string) => {
    try {
      const pin = pins.find(p => p.id === id);
      if (!pin) return;
      
      const { error } = await supabase
        .from('pins')
        .update({ archived: !pin.archived })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(pin.archived ? 'Pin restaurado' : 'Pin arquivado');
      fetchPins(); // Recarregar pins após arquivar/desarquivar
    } catch (error) {
      console.error('Erro ao arquivar pin:', error);
      toast.error('Erro ao arquivar/restaurar pin');
    }
  };
  
  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('pins')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success('Pin excluído com sucesso');
      fetchPins(); // Recarregar pins após excluir
    } catch (error) {
      console.error('Erro ao excluir pin:', error);
      toast.error('Erro ao excluir pin');
    }
  };
  
  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setIsAnalyticsLoading(true);
    
    // Em uma aplicação real, recarregaríamos os dados analíticos com base no intervalo de datas
    setTimeout(() => {
      // Gerar dados fictícios para o intervalo de datas selecionado
      const mockData: AnalyticsData[] = [];
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        mockData.push({
          date: format(currentDate, 'yyyy-MM-dd'),
          views: Math.floor(Math.random() * 100),
          likes: Math.floor(Math.random() * 50),
          comments: Math.floor(Math.random() * 20),
          shares: Math.floor(Math.random() * 10),
          sales: Math.floor(Math.random() * 5),
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setAnalyticsData(mockData);
      setIsAnalyticsLoading(false);
    }, 1000);
  };

  // Safe render - only render if navigate is available
  return (
    <DashboardLayout title="Meus Pins">
      <div className="space-y-8">
        <AnalyticsDashboard 
          data={analyticsData} 
          contentType="pin" 
          onDateRangeChange={handleDateRangeChange}
          isLoading={isAnalyticsLoading}
        />
        
        <Tabs defaultValue="active">
          <TabsList className="mb-6">
            <TabsTrigger value="active">Ativos</TabsTrigger>
            <TabsTrigger value="archived">Arquivados</TabsTrigger>
          </TabsList>
          
          <TabsContent value="active" className="mt-0">
            <ContentManagementGrid
              items={activePins}
              contentType="pin"
              onArchive={handleArchive}
              onDelete={handleDelete}
              onEdit={(id) => navigate(`/create/pin?edit=${id}`)}
              refreshData={fetchPins}
              isLoading={isLoading}
            />
          </TabsContent>
          
          <TabsContent value="archived" className="mt-0">
            <ContentManagementGrid
              items={archivedPins}
              contentType="pin"
              onArchive={handleArchive}
              onDelete={handleDelete}
              onEdit={(id) => navigate(`/create/pin?edit=${id}`)}
              refreshData={fetchPins}
              isLoading={isLoading}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default MyPins;
